import { useEffect, useState } from "react";
import {
  adminLogin, adminLogout, adminVerify,
  adminList, adminUpdate, adminDelete,
  listLogos, adminCreateLogo, adminUpdateLogo, adminDeleteLogo,
  adminListBlog, adminCreateBlog, adminUpdateBlog, adminDeleteBlog,
  adminUpdateSite, api,
} from "@/lib/api";import { useSite } from "@/lib/site";
import CitiesPanel from "@/components/admin/CitiesPanel";
import { toast } from "sonner";
import { MapPin, Trash2, CheckCircle2, Clock, LogOut, Lock, Mail, Phone, Plus, Pencil, X, Image as ImageIcon, MessageSquare, FileText, Eye, EyeOff, Settings, Save } from "lucide-react";

const SESSION_KEY = "dh_admin_session";
const TABS = [
  { id: "submissions", label: "Submissions", icon: MessageSquare },
  { id: "site", label: "Site Content", icon: Settings },
  { id: "logos", label: "Logos & Partners", icon: ImageIcon },
  { id: "blog", label: "Blog Posts", icon: FileText },
  { id: "cities", label: "Cities", icon: MapPin },
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [user, setUser] = useState(null);
  const [inputToken, setInputToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [tab, setTab] = useState("submissions");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // If we have a session id, verify with the server before showing the panel
    const sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) return;
    adminVerify()
      .then((res) => { setUser(res.user || null); setAuthed(true); })
      .catch((err) => {
        console.warn("[admin] Session invalid or expired:", err?.response?.status || err?.message);
        sessionStorage.removeItem(SESSION_KEY);
      });
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await adminLogin(inputToken);
      sessionStorage.setItem(SESSION_KEY, res.session_id);
      setUser(res.user || null);
      setAuthed(true);
      setInputToken("");
    } catch {
      toast.error("Invalid token");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    try { await adminLogout(); } catch { /* ignore */ }
    sessionStorage.removeItem(SESSION_KEY);
    setAuthed(false);
    setUser(null);
  };

  if (!authed) return (
    <AdminLogin
      busy={busy} login={login} inputToken={inputToken} setInputToken={setInputToken}
      showToken={showToken} setShowToken={setShowToken}
    />
  );

  return (
    <div className="container-x py-12" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--dh-navy)]">Admin Panel</h1>
          <p className="text-[color:var(--dh-slate)] text-sm mt-1">Manage submissions, content, logos, blog and cities.</p>
        </div>
        <div className="flex items-center gap-3">
          <AdminUserBadge user={user} />
          <button onClick={logout} className="btn-ghost-dark" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[color:var(--dh-line)] mb-8 overflow-x-auto" data-testid="admin-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-3 font-semibold text-sm inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                active ? "border-[color:var(--dh-orange)] text-[color:var(--dh-navy)]" : "border-transparent text-[color:var(--dh-slate)] hover:text-[color:var(--dh-navy)]"
              }`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "submissions" && <Submissions />}
      {tab === "site" && <SiteContentPanel />}
      {tab === "logos" && <LogosPanel />}
      {tab === "blog" && <BlogPanel />}
      {tab === "cities" && <CitiesPanel />}
    </div>
  );
}

function AdminLogin({ busy, login, inputToken, setInputToken, showToken, setShowToken }) {
  return (
    <div className="min-h-[70vh] grid place-items-center px-4" data-testid="admin-login">
      <div className="w-full max-w-sm p-8 rounded-3xl border border-[color:var(--dh-line)] bg-white">
        <div className="w-12 h-12 rounded-xl bg-[color:var(--dh-navy)] text-white grid place-items-center"><Lock className="w-5 h-5" /></div>
        <h1 className="mt-4 font-display text-2xl text-[color:var(--dh-navy)]">Admin Access</h1>
        <p className="text-sm text-[color:var(--dh-slate)]">Sign in with your authorised Google account.</p>
        <button type="button" onClick={startGoogleLogin} data-testid="admin-google-login-btn"
          className="mt-5 w-full inline-flex items-center justify-center gap-3 px-4 py-3 rounded-full border border-[color:var(--dh-line)] bg-white font-semibold text-[color:var(--dh-navy)] hover:border-[color:var(--dh-orange)] hover:shadow-md transition-[border-color,box-shadow]">
          <GoogleIcon /> Sign in with Google
        </button>
        <button type="button" onClick={() => setShowToken(!showToken)} data-testid="admin-token-toggle"
          className="mt-4 w-full text-xs text-[color:var(--dh-slate)] hover:text-[color:var(--dh-orange)] underline underline-offset-4">
          {showToken ? "Hide token sign-in" : "Use admin token instead"}
        </button>
        {showToken && (
          <form onSubmit={login} data-testid="admin-token-form">
            <input
              type="password" placeholder="Admin token" value={inputToken}
              onChange={(e) => setInputToken(e.target.value)} data-testid="admin-token-input"
              className="w-full mt-3 px-4 py-3 rounded-xl border border-[color:var(--dh-line)] focus:outline-none focus:border-[color:var(--dh-orange)]"
              autoComplete="off"
            />
            <button disabled={busy} className="btn-primary mt-3 w-full justify-center" data-testid="admin-login-btn">
              {busy ? "Signing in..." : "Sign in with token"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function AdminUserBadge({ user }) {
  if (!user || user.method !== "google") return <span className="chip" data-testid="admin-user-badge">Token session</span>;
  return (
    <div className="flex items-center gap-2" data-testid="admin-user-badge">
      {user.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" /> : null}
      <div className="leading-tight text-right">
        <div className="text-sm font-semibold text-[color:var(--dh-navy)]">{user.name}</div>
        <div className="text-xs text-[color:var(--dh-slate)]" data-testid="admin-user-email">{user.email}</div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

function Submissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try { setItems(await adminList()); }
    catch { toast.error("Failed to load submissions"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleStatus = async (item) => {
    const next = item.status === "new" ? "read" : "new";
    try { await adminUpdate(item.id, next); toast.success("Updated"); fetchList(); }
    catch { toast.error("Update failed"); }
  };

  const remove = async (item) => {
    if (!confirm("Delete this submission?")) return;
    try { await adminDelete(item.id); toast.success("Deleted"); fetchList(); }
    catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="text-center text-[color:var(--dh-slate)]">Loading…</div>;

  return (
    <div>
      <div className="text-sm text-[color:var(--dh-slate)] mb-4">{items.length} total · {items.filter(i => i.status === "new").length} new</div>
      {items.length === 0 ? (
        <div className="p-12 text-center border border-[color:var(--dh-line)] rounded-2xl">
          <div className="text-[color:var(--dh-slate)]">No submissions yet.</div>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((it) => (
            <div key={it.id} className="p-5 rounded-2xl border border-[color:var(--dh-line)] bg-white" data-testid={`admin-item-${it.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-lg text-[color:var(--dh-navy)]">{it.name}</div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${it.status === "new" ? "bg-orange-100 text-[color:var(--dh-orange)]" : "bg-green-100 text-green-700"}`}>
                      {it.status}
                    </span>
                    {it.service && <span className="chip">{it.service}</span>}
                    {it.city && <span className="chip" data-testid={`submission-city-${it.id}`}>{it.city}</span>}
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-sm text-[color:var(--dh-slate)] flex-wrap">
                    <a href={`mailto:${it.email}`} className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {it.email}</a>
                    {it.phone && <a href={`tel:${it.phone}`} className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {it.phone}</a>}
                    <span className="text-xs">{new Date(it.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => toggleStatus(it)} className="px-3 py-2 rounded-lg border border-[color:var(--dh-line)] hover:border-[color:var(--dh-orange)] text-sm inline-flex items-center gap-1" data-testid={`toggle-${it.id}`}>
                    {it.status === "new" ? <><CheckCircle2 className="w-4 h-4" /> Mark read</> : <><Clock className="w-4 h-4" /> Mark new</>}
                  </button>
                  <button onClick={() => remove(it)} className="px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm inline-flex items-center gap-1" data-testid={`delete-${it.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="mt-3 text-[color:var(--dh-ink)] whitespace-pre-wrap">{it.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const emptyLogo = { name: "", src: "", kind: "partner", order: 0 };

function LogosPanel() {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // logo obj or null
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyLogo);

  const fetchList = async () => {
    setLoading(true);
    try { setLogos(await listLogos()); }
    catch { toast.error("Failed to load logos"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => { setEditing(null); setForm(emptyLogo); setShowForm(true); };
  const openEdit = (l) => { setEditing(l); setForm({ name: l.name, src: l.src, kind: l.kind, order: l.order }); setShowForm(true); };
  const close = () => { setShowForm(false); setEditing(null); setForm(emptyLogo); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await adminUpdateLogo(editing.id, form);
        toast.success("Updated");
      } else if (form._file) {
        // Upload path — send multipart to /api/admin/logos/upload
        const fd = new FormData();
        fd.append("file", form._file);
        fd.append("name", form.name);
        fd.append("kind", form.kind);
        fd.append("order", String(form.order || 0));
        await api.post("/admin/logos/upload", fd);
        toast.success("Uploaded");
      } else if (form.src) {
        // Legacy URL path
        await adminCreateLogo({ name: form.name, src: form.src, kind: form.kind, order: form.order });
        toast.success("Added");
      } else {
        toast.error("Please choose a file or paste an image URL");
        return;
      }
      close(); fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (l) => {
    if (!confirm(`Delete "${l.name}"?`)) return;
    try { await adminDeleteLogo(l.id); toast.success("Deleted"); fetchList(); }
    catch { toast.error("Delete failed"); }
  };

  const partners = logos.filter((l) => l.kind === "partner");
  const clients = logos.filter((l) => l.kind === "client");

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-[color:var(--dh-slate)]">{partners.length} partners · {clients.length} clients</div>
        <button onClick={openNew} className="btn-primary" data-testid="logo-add">
          <Plus className="w-4 h-4" /> Add Logo
        </button>
      </div>

      {loading ? (
        <div className="text-center text-[color:var(--dh-slate)]">Loading…</div>
      ) : (
        <div className="space-y-10">
          {[
            { title: "Partners", items: partners, key: "partner" },
            { title: "Clients", items: clients, key: "client" },
          ].map((sec) => (
            <div key={sec.key} data-testid={`logo-section-${sec.key}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-xl text-[color:var(--dh-navy)]">{sec.title}</h2>
              </div>
              {sec.items.length === 0 ? (
                <div className="p-8 text-center border border-dashed border-[color:var(--dh-line)] rounded-2xl text-sm text-[color:var(--dh-slate)]">No {sec.title.toLowerCase()} yet.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {sec.items.map((l) => (
                    <div key={l.id} className="p-4 rounded-2xl border border-[color:var(--dh-line)] bg-white" data-testid={`logo-item-${l.id}`}>
                      <div className="aspect-square rounded-lg bg-[color:var(--dh-cream)] p-3 grid place-items-center overflow-hidden">
                        <img src={l.src} alt={l.name} className="max-w-full max-h-full object-contain" />
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-[color:var(--dh-navy)] truncate">{l.name}</div>
                          <div className="text-xs text-[color:var(--dh-slate)]">Order: {l.order}</div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => openEdit(l)} className="p-2 rounded-lg border border-[color:var(--dh-line)] hover:border-[color:var(--dh-orange)]" data-testid={`logo-edit-${l.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => remove(l)} className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50" data-testid={`logo-delete-${l.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={close} data-testid="logo-form-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-3xl p-6 w-full max-w-md border border-[color:var(--dh-line)]">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl text-[color:var(--dh-navy)]">{editing ? "Edit Logo" : "Add Logo"}</div>
              <button type="button" onClick={close} className="p-2 rounded-lg hover:bg-gray-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <label className="block">
                <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--dh-navy)] mb-1.5">Name</div>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="logo-name" className="w-full px-4 py-2.5 rounded-lg border border-[color:var(--dh-line)] focus:outline-none focus:border-[color:var(--dh-orange)]" />
              </label>
              <label className="block">
                <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--dh-navy)] mb-1.5">
                  {editing ? "Image URL" : "Upload a logo file (or paste image URL below)"}
                </div>
                {!editing && (
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    data-testid="logo-file"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const url = URL.createObjectURL(f);
                      setForm({ ...form, _file: f, src: url, name: form.name || f.name.replace(/\.[a-z0-9]+$/i, "") });
                    }}
                    className="w-full text-sm mb-2 file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-[color:var(--dh-orange)] file:text-white file:font-semibold file:cursor-pointer"
                  />
                )}
                <input value={editing ? form.src : (form._file ? "" : form.src)} onChange={(e) => setForm({ ...form, src: e.target.value, _file: null })} placeholder="…or paste https://…" data-testid="logo-src" className="w-full px-4 py-2.5 rounded-lg border border-[color:var(--dh-line)] focus:outline-none focus:border-[color:var(--dh-orange)]" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--dh-navy)] mb-1.5">Kind</div>
                  <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="logo-kind" className="w-full px-4 py-2.5 rounded-lg border border-[color:var(--dh-line)] focus:outline-none focus:border-[color:var(--dh-orange)] bg-white">
                    <option value="partner">Partner</option>
                    <option value="client">Client</option>
                  </select>
                </label>
                <label className="block">
                  <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--dh-navy)] mb-1.5">Order</div>
                  <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || 0, 10) })} data-testid="logo-order" className="w-full px-4 py-2.5 rounded-lg border border-[color:var(--dh-line)] focus:outline-none focus:border-[color:var(--dh-orange)]" />
                </label>
              </div>
              {form.src && (
                <div className="rounded-lg bg-[color:var(--dh-cream)] p-3 grid place-items-center h-24">
                  <img src={form.src} alt="preview" className="max-w-full max-h-full object-contain" />
                </div>
              )}
            </div>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={close} className="btn-ghost-dark flex-1 justify-center">Cancel</button>
              <button type="submit" className="btn-primary flex-1 justify-center" data-testid="logo-save">{editing ? "Save" : "Add"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const emptyPost = {
  slug: "", title: "", date: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }),
  category: "Latest News", excerpt: "", content: "", image: "", published: true,
};

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").slice(0, 100);
}

function BlogPanel() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyPost);
  const [slugTouched, setSlugTouched] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try { setPosts(await adminListBlog()); }
    catch { toast.error("Failed to load blog posts"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => { setEditing(null); setForm(emptyPost); setSlugTouched(false); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ slug: p.slug, title: p.title, date: p.date, category: p.category, excerpt: p.excerpt, content: p.content, image: p.image, published: p.published });
    setSlugTouched(true);
    setShowForm(true);
  };
  const close = () => { setShowForm(false); setEditing(null); setForm(emptyPost); setSlugTouched(false); };

  const setField = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: v };
      // auto-generate slug from title while creating & untouched
      if (k === "title" && !editing && !slugTouched) next.slug = slugify(v);
      return next;
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.slug || !form.image || !form.excerpt || !form.content) {
      toast.error("Please fill title, slug, image, excerpt and content."); return;
    }
    try {
      if (editing) { await adminUpdateBlog(editing.id, form); toast.success("Post updated"); }
      else { await adminCreateBlog(form); toast.success("Post created"); }
      close(); fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const togglePublish = async (p) => {
    try { await adminUpdateBlog(p.id, { published: !p.published }); toast.success(p.published ? "Unpublished" : "Published"); fetchList(); }
    catch { toast.error("Update failed"); }
  };

  const remove = async (p) => {
    if (!confirm(`Delete "${p.title}"?`)) return;
    try { await adminDeleteBlog(p.id); toast.success("Deleted"); fetchList(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="text-sm text-[color:var(--dh-slate)]">{posts.length} total · {posts.filter((p) => p.published).length} published</div>
        <button onClick={openNew} className="btn-primary" data-testid="blog-add">
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      {(() => {
        if (loading) return <div className="text-center text-[color:var(--dh-slate)]">Loading…</div>;
        if (posts.length === 0) return (
          <div className="p-12 text-center border border-[color:var(--dh-line)] rounded-2xl text-[color:var(--dh-slate)]">No posts yet.</div>
        );
        return (
          <div className="space-y-3">
            {posts.map((p) => (
            <div key={p.id} className="p-4 rounded-2xl border border-[color:var(--dh-line)] bg-white flex gap-4 items-center" data-testid={`blog-item-${p.id}`}>
              <img src={p.image} alt={p.title} className="w-24 h-16 object-cover rounded-lg shrink-0 bg-[color:var(--dh-cream)]" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-bold text-[color:var(--dh-navy)] truncate">{p.title}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${p.published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                    {p.published ? "Published" : "Draft"}
                  </span>
                  <span className="chip">{p.category}</span>
                </div>
                <div className="text-xs text-[color:var(--dh-slate)] mt-1 truncate">/blog/{p.slug} · {p.date}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => togglePublish(p)} className="p-2 rounded-lg border border-[color:var(--dh-line)] hover:border-[color:var(--dh-orange)]" data-testid={`blog-toggle-${p.id}`} title={p.published ? "Unpublish" : "Publish"}>
                  {p.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button onClick={() => openEdit(p)} className="p-2 rounded-lg border border-[color:var(--dh-line)] hover:border-[color:var(--dh-orange)]" data-testid={`blog-edit-${p.id}`} title="Edit">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(p)} className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50" data-testid={`blog-delete-${p.id}`} title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          </div>
        );
      })()}

      {showForm && (
        <BlogFormModal
          editing={editing}
          form={form}
          setForm={setForm}
          setSlugTouched={setSlugTouched}
          setField={setField}
          onClose={close}
          onSave={save}
        />
      )}

      <style>{`.input-admin { width:100%; padding:.7rem 1rem; border-radius:12px; border:1px solid var(--dh-line); background:#fff; font-size:14px; color:var(--dh-ink); transition:border-color .2s ease; } .input-admin:focus { outline:none; border-color: var(--dh-orange); box-shadow: 0 0 0 3px rgba(255,107,26,0.15); }`}</style>
    </div>
  );
}


function BlogFormModal({ editing, form, setForm, setSlugTouched, setField, onClose, onSave }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="blog-form-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSave} className="bg-white rounded-3xl p-6 w-full max-w-2xl border border-[color:var(--dh-line)] my-8">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl text-[color:var(--dh-navy)]">{editing ? "Edit Post" : "New Blog Post"}</div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3">
          <FormField label="Title *">
            <input required value={form.title} onChange={setField("title")} data-testid="blog-title" className="input-admin" placeholder="Post title" />
          </FormField>
          <div className="grid sm:grid-cols-2 gap-3">
            <FormField label="Slug *">
              <input required value={form.slug} onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: slugify(e.target.value) }); }} data-testid="blog-slug" className="input-admin" placeholder="my-post-slug" />
            </FormField>
            <FormField label="Category *">
              <input required value={form.category} onChange={setField("category")} data-testid="blog-category" className="input-admin" placeholder="e.g. Technology" />
            </FormField>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <FormField label="Date *">
              <input required value={form.date} onChange={setField("date")} data-testid="blog-date" className="input-admin" placeholder="e.g. 27 January 2026" />
            </FormField>
            <FormField label="Published">
              <label className="flex items-center gap-2 mt-2.5">
                <input type="checkbox" checked={form.published} onChange={setField("published")} data-testid="blog-published" className="w-4 h-4 accent-[color:var(--dh-orange)]" />
                <span className="text-sm text-[color:var(--dh-slate)]">{form.published ? "Visible on site" : "Draft — hidden"}</span>
              </label>
            </FormField>
          </div>
          <FormField label="Image URL *">
            <input required value={form.image} onChange={setField("image")} data-testid="blog-image" className="input-admin" placeholder="https://…" />
          </FormField>
          {form.image && (
            <div className="rounded-lg bg-[color:var(--dh-cream)] p-2 h-32 grid place-items-center overflow-hidden">
              <img src={form.image} alt="preview" className="max-w-full max-h-full object-contain" />
            </div>
          )}
          <FormField label="Excerpt * (1–2 sentence summary)">
            <textarea required rows={2} value={form.excerpt} onChange={setField("excerpt")} data-testid="blog-excerpt" className="input-admin resize-none" placeholder="Short summary shown on the blog listing" />
          </FormField>
          <FormField label="Content * (double-newline for new paragraphs)">
            <textarea required rows={8} value={form.content} onChange={setField("content")} data-testid="blog-content" className="input-admin resize-none" placeholder="Full post content…" />
          </FormField>
        </div>

        <div className="mt-5 flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost-dark flex-1 justify-center">Cancel</button>
          <button type="submit" className="btn-primary flex-1 justify-center" data-testid="blog-save">{editing ? "Save changes" : "Publish"}</button>
        </div>
      </form>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--dh-navy)] mb-1.5">{label}</div>
      {children}
    </label>
  );
}



function SiteContentPanel() {
  const { site, reloadSite } = useSite();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // deep-clone site into local draft AND back-fill stable _uid on each row
    // so React lists get proper keys even when rows are added/removed/reordered.
    const withUids = (arr) =>
      (arr || []).map((x) => (x && x._uid ? x : { ...x, _uid: (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`) }));
    const cloned = JSON.parse(JSON.stringify(site || {}));
    if (cloned.contact) cloned.contact.hours = withUids(cloned.contact.hours);
    if (cloned.hero) cloned.hero.stats = withUids(cloned.hero.stats);
    cloned.testimonials = withUids(cloned.testimonials);
    setDraft(cloned);
  }, [site]);

  if (!draft) return <div className="text-center text-[color:var(--dh-slate)]">Loading…</div>;

  const newUid = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;

  const setPath = (path, value) => {
    setDraft((d) => {
      const next = { ...d };
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...(obj[keys[i]] || {}) };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Strip client-side _uid before persisting — server doesn't need it.
      const strip = (arr) => (arr || []).map(({ _uid, ...rest }) => rest);
      const payload = { ...draft };
      if (payload.contact) payload.contact = { ...payload.contact, hours: strip(payload.contact.hours) };
      if (payload.hero) payload.hero = { ...payload.hero, stats: strip(payload.hero.stats) };
      payload.testimonials = strip(payload.testimonials);
      await adminUpdateSite(payload);
      await reloadSite();
      toast.success("Site content saved");
    } catch (e) {
      console.warn("[admin] Save failed:", e?.response?.status || e?.message);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const c = draft.contact || {};
  const s = draft.socials || {};
  const w = draft.whatsapp || {};
  const b = draft.branding || {};
  const h = draft.hero || {};
  const a = draft.about || {};
  const hours = c.hours || [];
  const stats = h.stats || [];
  const testis = draft.testimonials || [];

  return (
    <div className="space-y-8" data-testid="site-content-panel">
      <Section title="Branding">
        <Row>
          <Field label="Company name"><input className="input-admin" value={b.company_name || ""} onChange={(e) => setPath("branding.company_name", e.target.value)} data-testid="site-company_name" /></Field>
          <Field label="Short name"><input className="input-admin" value={b.short_name || ""} onChange={(e) => setPath("branding.short_name", e.target.value)} data-testid="site-short_name" /></Field>
          <Field label="Tagline"><input className="input-admin" value={b.tagline || ""} onChange={(e) => setPath("branding.tagline", e.target.value)} data-testid="site-tagline" /></Field>
        </Row>
      </Section>

      <Section title="Contact info">
        <Row>
          <Field label="Phone 1"><input className="input-admin" value={c.phone1 || ""} onChange={(e) => setPath("contact.phone1", e.target.value)} data-testid="site-phone1" /></Field>
          <Field label="Phone 2"><input className="input-admin" value={c.phone2 || ""} onChange={(e) => setPath("contact.phone2", e.target.value)} data-testid="site-phone2" /></Field>
          <Field label="Email"><input className="input-admin" value={c.email || ""} onChange={(e) => setPath("contact.email", e.target.value)} data-testid="site-email" /></Field>
        </Row>
        <Field label="Address"><textarea rows={2} className="input-admin resize-none" value={c.address || ""} onChange={(e) => setPath("contact.address", e.target.value)} data-testid="site-address" /></Field>
        <div className="mt-3">
          <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--dh-navy)] mb-2">Business hours</div>
          <div className="space-y-2">
            {hours.map((row, i) => (
              <div key={row._uid} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                <input className="input-admin" value={row.day} onChange={(e) => { const next = [...hours]; next[i] = { ...next[i], day: e.target.value }; setPath("contact.hours", next); }} placeholder="Day range" />
                <input className="input-admin" value={row.time} onChange={(e) => { const next = [...hours]; next[i] = { ...next[i], time: e.target.value }; setPath("contact.hours", next); }} placeholder="Time" />
                <button onClick={() => setPath("contact.hours", hours.filter((_, j) => j !== i))} className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setPath("contact.hours", [...hours, { day: "", time: "", _uid: newUid() }])} className="btn-ghost-dark text-sm"><Plus className="w-4 h-4" /> Add row</button>
          </div>
        </div>
      </Section>

      <Section title="Social media URLs">
        <Row>
          <Field label="Facebook"><input className="input-admin" value={s.facebook || ""} onChange={(e) => setPath("socials.facebook", e.target.value)} data-testid="site-facebook" /></Field>
          <Field label="Instagram"><input className="input-admin" value={s.instagram || ""} onChange={(e) => setPath("socials.instagram", e.target.value)} data-testid="site-instagram" /></Field>
          <Field label="LinkedIn"><input className="input-admin" value={s.linkedin || ""} onChange={(e) => setPath("socials.linkedin", e.target.value)} data-testid="site-linkedin" /></Field>
        </Row>
      </Section>

      <Section title="WhatsApp click-to-chat">
        <Row>
          <Field label="Number (E.164, no + or spaces)"><input className="input-admin" value={w.number || ""} onChange={(e) => setPath("whatsapp.number", e.target.value.replace(/[^0-9]/g, ""))} data-testid="site-wa-number" placeholder="919808724448" /></Field>
          <Field label="Pre-filled message"><input className="input-admin" value={w.message || ""} onChange={(e) => setPath("whatsapp.message", e.target.value)} data-testid="site-wa-message" /></Field>
        </Row>
      </Section>

      <Section title="Hero section">
        <Row>
          <Field label="Eyebrow"><input className="input-admin" value={h.eyebrow || ""} onChange={(e) => setPath("hero.eyebrow", e.target.value)} /></Field>
          <Field label="Primary CTA text"><input className="input-admin" value={h.cta_primary || ""} onChange={(e) => setPath("hero.cta_primary", e.target.value)} /></Field>
          <Field label="Secondary CTA text"><input className="input-admin" value={h.cta_secondary || ""} onChange={(e) => setPath("hero.cta_secondary", e.target.value)} /></Field>
        </Row>
        <div className="mt-3">
          <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--dh-navy)] mb-2">Stats (numbers + label)</div>
          <div className="space-y-2">
            {stats.map((row, i) => (
              <div key={row._uid} className="grid grid-cols-[1fr_2fr_auto] gap-2">
                <input className="input-admin" value={row.n} onChange={(e) => { const next = [...stats]; next[i] = { ...next[i], n: e.target.value }; setPath("hero.stats", next); }} placeholder="6+" />
                <input className="input-admin" value={row.l} onChange={(e) => { const next = [...stats]; next[i] = { ...next[i], l: e.target.value }; setPath("hero.stats", next); }} placeholder="Years in Business" />
                <button onClick={() => setPath("hero.stats", stats.filter((_, j) => j !== i))} className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setPath("hero.stats", [...stats, { n: "", l: "", _uid: newUid() }])} className="btn-ghost-dark text-sm"><Plus className="w-4 h-4" /> Add stat</button>
          </div>
        </div>
      </Section>

      <Section title="About section">
        <Row>
          <Field label="Eyebrow"><input className="input-admin" value={a.eyebrow || ""} onChange={(e) => setPath("about.eyebrow", e.target.value)} /></Field>
          <Field label="Heading"><input className="input-admin" value={a.heading || ""} onChange={(e) => setPath("about.heading", e.target.value)} /></Field>
          <Field label="Highlight"><input className="input-admin" value={a.highlight || ""} onChange={(e) => setPath("about.highlight", e.target.value)} /></Field>
        </Row>
        <Field label="Paragraph 1"><textarea rows={3} className="input-admin resize-none" value={a.paragraph1 || ""} onChange={(e) => setPath("about.paragraph1", e.target.value)} /></Field>
        <Field label="Paragraph 2"><textarea rows={3} className="input-admin resize-none" value={a.paragraph2 || ""} onChange={(e) => setPath("about.paragraph2", e.target.value)} /></Field>
      </Section>

      <Section title="Testimonials">
        <div className="space-y-3">
          {testis.map((t, i) => (
            <div key={t._uid} className="p-4 border border-[color:var(--dh-line)] rounded-2xl space-y-2 bg-white">
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-[color:var(--dh-navy)] text-sm">Testimonial #{i + 1}</div>
                <button onClick={() => setPath("testimonials", testis.filter((_, j) => j !== i))} className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <Row>
                <Field label="Name"><input className="input-admin" value={t.name || ""} onChange={(e) => { const next = [...testis]; next[i] = { ...next[i], name: e.target.value }; setPath("testimonials", next); }} /></Field>
                <Field label="Role"><input className="input-admin" value={t.role || ""} onChange={(e) => { const next = [...testis]; next[i] = { ...next[i], role: e.target.value }; setPath("testimonials", next); }} /></Field>
              </Row>
              <Field label="Quote"><textarea rows={2} className="input-admin resize-none" value={t.quote || ""} onChange={(e) => { const next = [...testis]; next[i] = { ...next[i], quote: e.target.value }; setPath("testimonials", next); }} /></Field>
            </div>
          ))}
          <button onClick={() => setPath("testimonials", [...testis, { name: "", role: "", quote: "", _uid: newUid() }])} className="btn-ghost-dark text-sm"><Plus className="w-4 h-4" /> Add testimonial</button>
        </div>
      </Section>

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary shadow-lg" data-testid="site-save">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save all changes"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="p-5 border border-[color:var(--dh-line)] rounded-2xl bg-white space-y-3">
      <h2 className="font-display text-lg text-[color:var(--dh-navy)]">{title}</h2>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">{children}</div>;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--dh-navy)] mb-1.5">{label}</div>
      {children}
    </label>
  );
}

